import { defineStore } from 'pinia'
import { searchTagSetup, displayTagSetupMap, displayTagSetupDocument, validateDisplayKey, getSearchKeyType, getSearchPairType, getDisplayKeyType, displayDataSetup, searchKeyTypes, getKeyType } from './arkivMetaDef'

export const useArkivMetaStore = defineStore('ArkivMetaStore', {
  /**
 * Initial reactive state for the `arkivMetaStore`, containing flags, parameters,
 * and metadata used for search and display logic in the application.
 * 
 * @property {boolean} isKort - Determines if the current search context is for maps (kort) or documents (dokumenter).
 * @property {Object} searchTags - Stores the current values for search filters (dynamic, based on `searchTagSetup` from `arkivMetaDef`).
 * @property {Object} displayTags - Stores UI-level query parameters like sorting, offset, and limit (based on `displayTagSetup` from `arkivMetaDef`).
 * @property {string} queryString - The raw query string representation of the current store state to send to arkivmeta v3
 * @property {Object} urlParams - Parsed representation of `queryString` as key-value pairs.
 * @property {Object} responseData - Contains the latest fetched result set.
 * @property {Object} responseData.value - The actual payload returned by arkivmeta v3.
 * @property {boolean} responseData.isLoading - Indicates if data is currently being fetched from arkvimeta v3.
 * @property {string} responseData.error - Stores any error messages resulting from a fetch.
 * @property {Object} displayMetadata - Contains static metadata from arkivmeta v3 for populating filter UIs, initialized via `displayDataSetup`.
 */
  state: () => ({
    isKort: true,
    searchTags: {},
    displayTags: {},
    queryString: '',
    urlParams: {},
    responseData: {
      value: {},
      isLoading: false,
      error: ''
    },
    displayMetadata: displayDataSetup
  }),
  getters: {
  /**
   * Retrieves static metadata from arkivmeta v3 used to populate valid search and sort tag options on the website.
   * 
   * The data is fetched during `fetchDisplayMetaData` and stored under `displayMetadata`.
   * 
   * If the key is invalid or the data has not yet been loaded, it returns an empty array.
   *
   * @param {string} key - The display metadata key (e.g., `'kortgrupper'`, `'sognenavn'`, etc.)
   * @returns {Array} The metadata associated with the key, or an empty array if unavailable.
   */
    getDisplayMetadataItem: (state) => (key) => {
      return state.displayMetadata[key]?.value ?? []
    },
    /**
   * Retrieves the payload returned from arkivmeta v3 fetched using the `sendQuery` function.
   * 
   * Depending on the search context (`isKort`), it returns either the `.kort` or `.dokumenter` property 
   * from the response. If no successful response has been received, it returns an empty array.
   *
   * @returns {Array} An array of metadata objects representing maps or documents, based on the current
   * `searchTags` and `displayTags` configuration.
   */
    getDisplayData: (state) => () => {
      return state.isKort ? state.responseData.value?.kort || [] : state.responseData.value?.dokumenter || []
    },
    /**
   * Retrieves the total number of search results available for the current query.
   * 
   * This value is extracted from the `.total` property of `.responseData`, which is populated
   * when the `sendQuery` function successfully fetches data.
   * 
   * If no successful response has been received, the method returns `0`.
   *
   * @returns {number} The total number of available search results.
   */
    getResultsTotal: (state) => () => {
      return state.responseData.value?.total || 0
    },
    /**
   * Returns display-level parameters (`limit`, `offset`, `sort`, `direction`), which control
   * pagination and sorting behavior for results returned by the Arkivmeta v3 API.
   *
   * These values are not filters but define how result sets are fetched and presented.
   *
   * @returns {Object} The current `displayTags` object, or an empty object if not set.
   */
    getDisplayTags: (state) => () => {
      return state.displayTags || {}
    },
    /**
   * Retrieves a specific display-level tag value (e.g. `limit`, `offset`, `sort`, or `direction`)
   * from the `displayTags` state, based on the provided key.
   *
   * This function only returns values for keys that are recognized as either string or number types 
   * within the `displayTags` schema. If the key is not valid, a warning is logged and `undefined` is returned.
   *
   * @param {string} key - The display tag key to retrieve. Must be of type `'string'` or `'number'` as defined in `displayKeyTypes`.
   * @returns {string | number | undefined} The value of the corresponding display tag, or `undefined` if the key is invalid.
   */
    getDisplayTag: (state) => (key) => {
      switch(getDisplayKeyType(key)){
      case 'string':
      case 'number': 
        return state.displayTags[key]
      default:
        console.warn(
          `[ArkivMetaStore] getDisplayTags: Invalid request. '${key}' not found in DislayTags. Undefined returned`
        )
        return undefined
      
      }
    },
    /**
     * Retrieves a Set-based value from `searchTags` using the specified key.
     * 
     * The key must be part of the known Set-based filters (`searchKeyTypes.setKeys`).
     * If the key is invalid or not defined in `searchTags`, an empty Set is returned.
     * 
     * @param {string} key - The key corresponding to a Set-based filter (e.g., `'kortgruppe'`, `'dokumentsamling'`).
     * @returns {Set<string>} A Set of strings representing the filter tag values for the given key,
     * or an empty Set if the key is invalid or not present.
     */
    getSearchKeySet: (state) => (key) => {
      if(!searchKeyTypes.setKeys.has(key)) {
        console.warn(
          `[ArkivMetaStore] getSearchKeySet: ${key} does not provide access to a set of filters`
        )
        return new Set()
      } 
      return state.searchTags[key] || new Set()
    },
    /**
     * Retrieves a number-based value from `searchTags` using the specified key.
     * 
     * The key must be included in the known number-based filters (`searchKeyTypes.numberKeys`).
     * If the key is invalid or not present in `searchTags`, `Number.NaN` is returned.
     * 
     * @param {string} key - The key corresponding to a number-based filter 
     * (e.g., `'gaeldendeperiode_gaeldendefra'`, `'herredsnummer'`).
     * @returns {number} The number value associated with the given key,
     * or `Number.NaN` if the key is invalid or unset.
     */
    getSearchKeyNumber: (state) => (key) => {
      if(!searchKeyTypes.numberKeys.has(key)) {
        console.warn(
          `[ArkivMetaStore] getSearchKeyNumber: ${key} does not provide access to a filter of type Number`
        )
        return Number.NaN
      }
      return state.searchTags[key] || Number.NaN
    },
    /**
     * Retrieves a string-based value from `searchTags` using the specified key.
     * 
     * The key must be included in the known string-based filters (`searchKeyTypes.stringKeys`).
     * If the key is invalid or not present in `searchTags`, an empty string is returned.
     * 
     * @param {string} key - The key corresponding to a string-based filter 
     * (e.g., `'fritekstsoegning'`, `'titel'`).
     * @returns {string} The string value associated with the given key,
     * or an empty string if the key is invalid or unset.
     */
    getSearchKeyString: (state) => (key) => {
      if(!searchKeyTypes.stringKeys.has(key)) {
        console.warn(
          `[ArkivMetaStore] getSearchKeyString: ${key} does not provide access to a filter of type String`
        )
        return ''
      }
      return state.searchTags[key] || ''
    },
    /**
     * Computes the number of active filter values for a given key in `searchTags`.
     *
     * The count logic is based on the type of the key:
     * - `set`: Returns the size of the Set.
     * - `string`: Returns 1 if the string is non-empty, otherwise 0.
     * - `number`: Returns 1 if the number is not NaN, otherwise 0.
     *
     * If the key is not recognized, 0 is returned and a warning is logged.
     *
     * @param {string} key - The key to evaluate in `searchTags`.
     * @returns {number} The number of active values associated with the key (0 or 1 for scalar values, size for sets).
     */
    getKeySearchTagCount: (state) => (key) => {
      switch(getSearchKeyType(key)){
      case 'set':
        return state.searchTags[key].size
      case 'string':
        return state.searchTags[key].trim() === '' ? 0 : 1
      case 'number':
        return Number.isNaN(state.searchTags[key]) ? 0 : 1
      default:
        console.warn(
          `[ArkivMetaStore] getKeySearchTagCount: Key ${key} was not found among valid keys `
        )
        return 0
      }
    },
    /**
   * Computes the total number of active search filters across all keys in `searchTags`.
   *
   * Uses `getKeySearchTagCount` to determine the contribution of each key
   * based on its filter type and whether a value is currently set.
   *
   * @returns {number} The total number of active filters in `searchTags`.
   */
    getSearchTagCount: (state) => () => {
      return Object.keys(state.searchTags).reduce(
        (sum, key) => {
          return sum + state.getKeySearchTagCount(key)
        }, 0
      )
    },
    /**
    * Returns the complete `searchTags` object from the store.
    *
    * For use cases that require access to specific tags, consider using:
    * - `getSearchKeyString` for string-based filters
    * - `getSearchKeySet` for set-based filters
    * - `getSearchKeyNumber` for number-based filters
    *
    * If you need flattened or structured filter data, use `getSearchTagPairs`
    * or define a custom getter tailored to your needs.
    *
    * @returns {Object} The full `searchTags` object, or an empty object if undefined.
    */
    getSearchTags: state => () => {
      return state.searchTags || {}
    },
    /**
     * Returns all active search filters as an array of `{ key, tag }` pairs.
     *
     * Each entry represents a single active filter:
     * - For `Set` values, each item in the set is returned as a separate `{ key, tag }` pair.
     * - For `string` values, only non-empty strings are included.
     * - For `number` values, only non-NaN numbers are included.
     *
     * This getter is primarily used in the `Filtertags.vue` component to display active filters
     * and enable their removal via the `removeSearchTag` function.
     *
     * @returns {Array<{ key: string, tag: string | number }>} An array of active filter key-tag pairs.
     */
    getSearchTagPairs: (state) => () => {
      const keyPairs = []
      Object.entries(state.searchTags).forEach(([key, tag]) => {
        switch(getSearchKeyType(key)){
        case 'set':
          tag.forEach(t => {
            keyPairs.push({key: key, tag: t})
          })
          break
        case 'string':
          if(tag.trim() === '') {
            break
          }
        case 'number':
          if(Number.isNaN(tag)){
            break
          }
          keyPairs.push({key: key, tag: tag})
          break
        default:
          break
        }
      })
      return keyPairs
    },
    /**
     * Returns the current search mode of the application.
     *
     * This flag determines the structure of the `searchTags` object and which type of result
     * (`kort` for maps or `dokumenter` for documents) is displayed from the `responseData`
     * returned by Arkivmeta v3.
     *
     * Use `setKortSearch()` or `setDokumentSearch()` in actions to switch between modes.
     *
     * @returns {boolean} `true` if in map ("kort") mode, `false` if in document mode.
     */
    getIsKort: (state) => () => {
      return state.isKort
    },
    /**
    * Returns the current `urlParams` object stored in the ArkivMetaStore.
    *
    * This reflects the latest URL query parameters synchronized with the store state.
    * It is primarily used to monitor external changes to the URL and keep the application state in sync.
    *
    * For example, in components like `MapSearch.vue` or `DocumentSearch.vue`, a watcher is set up
    * on `urlParams` to reset the search state when all urlParams have been cleared.
    *
    * @returns {Object} The current `urlParams` object representing the store's view of the URL query state.
    */
    getURLParams: (state) => () => {
      return state.urlParams
    }
  },
  actions: {
    /**
     * Fetches display metadata from the Arkivmeta API for a single display item.
     *
     * This is typically called during application startup (e.g., in `App.vue`) to populate UI components
     * with auxiliary metadata such as dropdown options or static display data.
     *
     * The function updates the passed `item` object in-place by setting:
     * - `isLoading` to `true` while the request is active
     * - `value` to the fetched JSON result if successful
     * - `error` with an HTTP status string if the request fails
     *
     * Any errors or failed HTTP responses are logged to the console.
     *
     * @async
     * @param {Object} item - A display metadata item object with the following shape:
     * @param {string} item.endpoint - The API endpoint to fetch data from.
     * @param {boolean} [item.isLoading] - Indicates if the item is currently being fetched.
     * @param {*} [item.value] - The resulting data populated after a successful request.
     * @param {string} [item.error] - Set if the request fails with an HTTP error.
     */    
    async fetchDisplayItem(item) {
      const url = `${import.meta.env.VITE_ARKIVMETA_URL}${item.endpoint}?token=${import.meta.env.VITE_ARKIVMETA_TOKEN}`
      try {
        item.isLoading = true
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Origin': window.location.origin
          },
          mode: 'cors',
          credentials: 'omit'
        })
        if (!response.ok) {
          console.warn(
            `[ArkivMetaStore] fetchDisplayItem: fetch failed for ${url}. Response was ${response.status} ${response.statusText}`
          )
          item.error = `HTTP ${response.status}`
          return
        }
        const data = await response.json()
        item.value = data
      } catch (err) {
        console.error(`[ArkivMetaStore] fetchDisplayItem: network error for ${item.endpoint}`, err)
      } finally {
        item.isLoading = false
      }
    },

    /**
     * Fetches all configured display metadata items in parallel.
     *
     * This function is used during application startup to populate the store's `displayMetadata` object
     * with values from the Arkivmeta API. It calls `fetchDisplayItem` for each metadata key.
     *
     * Typically invoked in `App.vue`'s `onMounted()` hook, this action ensures all static or UI-bound
     * metadata is loaded before the user begins interacting with the application.
     *
     * @async
     * @returns {Promise<void>} A promise that resolves when all display metadata items have been fetched.
    */
    async fetchDisplayMetaData() {
      await Promise.all(Object.keys(this.displayMetadata).map(key => this.fetchDisplayItem(this.displayMetadata[key])))
    },    
    /**
     * Initializes the store for **map search mode** (`kort`).
     *
     * This method sets `isKort` to `true`, configures `searchTags` by selecting default parameter states from `searchTagSetup`in arkivMetaDef.js.
     *
     * This action is typically invoked:
     * - At application startup when initializing or restoring search state.
     * - When switching from document mode to map mode.
     *
     * Logs the mode switch to the console.
     */
    setKortSearch() {
      /*
      * It is crucial to use the spread operator (`...`) when copying the default state
      * from `arkivMetaDef.js`, as a shallow copy is required to prevent unwanted reference mutation.
      */
      this.isKort = true
      this.searchTags = structuredClone({
        ...searchTagSetup.global,
        ...searchTagSetup.kort

      })
      this.displayTags = structuredClone({...displayTagSetupMap}, {deep: true})
      console.log('[arkivmetaStore]: Map Search Selected')
    },
    /**
     * Initializes the store for **Document search mode** (`Dokumenter`).
     *
     * This method sets `isKort` to `false`, configures `searchTags` by selecting default parameter states from `searchTagSetup`in arkivMetaDef.js.
     *
     * This action is typically invoked:
     * - At application startup when initializing or restoring search state.
     * - When switching from map mode to document mode.
     *
     * Logs the mode switch to the console.
     */
    setDokumentSearch() {
      this.isKort = false
      this.searchTags = structuredClone({
        ...searchTagSetup.global,
        ...searchTagSetup.dokument
      })
      this.displayTags = structuredClone({...displayTagSetupDocument}, {deep: true})
      console.log('[arkivmetaStore]: Document Search Selected')
    },
    /**
     * Resets the store state to the default configuration based on the current `isKort` mode.
     *
     * This function acts as a convenience wrapper that conditionally delegates to either
     * `setKortSearch()` or `setDokumentSearch()` depending on the value of `isKort`.
     *
     * Use this method to restore the store to default state (e.g., when clearing all filters),
     * while preserving the current search mode (map or document).
     */
    reset() {
      if(this.isKort){
        this.setKortSearch()
      }
      else {
        this.setDokumentSearch()
      }
    },
    /**
     * Adds a new tag value to the appropriate field in the `searchTags` object, based on its key and expected type.
     *
     * Behavior:
     * - If the `key` belongs to `searchKeyTypes.setKeys`, the `tag` is added to the corresponding `Set`.
     * - If the `key` is in `searchKeyTypes.stringKeys` or `numberKeys`, the existing value is overwritten with the new `tag`.
     * 
     * Type safety:
     * - The `getSearchPairType` utility is used to validate the compatibility of the provided `key` and `tag`.
     * - If the key/tag combination is invalid or does not match known types, a warning is logged and no changes are made.
     *
     * Common usage:
     * - Dynamically update search filters in the store when the user interacts with UI controls (checkboxes, inputs, etc)
     *   before calling the `sendQuery` action.
     * @param {string} key - The key in `searchTags` to which the tag should be added.
     * @param {Set|string|number} tag - The value to add or assign. Its type must match the expected type for the given key.
     */
    addSearchTag(key, tag) {
      switch(getSearchPairType(key, tag)){
      case 'set': 
        this.searchTags[key].add(tag)
        break
      case 'string':
      case 'number':
        this.searchTags[key] = tag
        break
      default:
        console.warn(
          `[ArkivMetaStore] addSearchTag: Key "${key}" and Tag ${tag} do not match any known type groups. No action taken.`
        )
      }
    },
    /**
     * Overwrites the Set stored under a given `searchTags` key with a new Set constructed from the provided `tagList`.
     *
     * This function is intended only for keys that store values of type `Set` (e.g. filters with multiple selections).
     * It replaces the entire Set rather than adding to it, allowing for atomic updates of multi-select filters.
     *
     * Type validation is done based on the type of the first element in the `tagList`. If the key is not associated
     * with a Set-type filter, a warning is logged and no update is performed.
     *
     * Common usage:
     * - Used to overwrite Set-type search filters with a completely new selection (e.g. in `MaalestokBar.vue`, 
     *   when a new range of scale values is selected and replaces the existing ones).
     * - Typically used before or together with `sendQuery` to request updated results.
     *
     * @param {string} key - The key in `searchTags` whose Set value should be replaced.
     * @param {Array<string>} tagList - An array of tags to be added as the new Set value.
     */
    addSearchTagList(key, tagList) {     
      if(Array.isArray(tagList), tagList.length > 0) {
        const searchKeyType = getSearchPairType(key, tagList[0][0])
        switch(searchKeyType){
        case 'set':
          this.searchTags[key] = new Set(tagList)
          break
        case 'string':
        case 'number':
        default:
          console.warn(
            `[ArkivMetaStore] addSearchTagList. Updates based on taglist only supported by tags of type 'set'. Type is ${searchKeyType}. Taglist is: ${tagList}`
          )
        }
      }
      else {
        console.warn(
          `[ArkivMetaStore] addSearchTagList: TagList must be of type Array and contain at least one element. No action taken. Taglist is: ${tagList}`
        )
      }
    },
    /**
     * Removes or clears the value associated with a given key in `searchTags`.
     *
     * This function handles different tag types:
     * - For `Set` keys: removes the specified tag from the Set.
     * - For `string` keys: clears the string by setting it to an empty string.
     * - For `number` keys: resets the value by setting it to `NaN`.
     *
     * If the key does not match any known type group, a warning is logged and no action is taken.
     *
     * Common usage:
     * - Used to remove individual filters when a user clicks a delete button (e.g. in `FilterTags.vue`)
     *   or unchecks a checkbox (e.g in `FilterBar.vue`).
     * - Typically used before or together with `sendQuery` to request updated results.
     *
     * @param {string} key - The key in `searchTags` to update.
     * @param {string|number} tag - The tag to remove or clear from the key's value.
     */    
    removeSearchTag(key, tag) {
      switch(getSearchKeyType(key)){
      case 'set':
        this.searchTags[key].delete(tag)
        break
      case 'string':
        this.searchTags[key] = ''
        break
      case 'number':
        this.searchTags[key] = Number.NaN
        break
      default: 
        console.warn(
          `[ArkivMetaStore] removeSearchTag: Key "${key}" and Tag ${tag} do not match any known type groups. No action taken.`
        )
        break
      }
    },
    /**
     * Updates the value of a display-related tag in `displayTags`.
     *
     * This function is used to modify display-related parameters for a given search request.
     * These parameters affect how results are presented, rather than what results are returned.
     *
     * Supported display keys include:
     * - `sort`: A metadata property to sort the results by (e.g. "Titel", "Kortgruppe", "Målestok").
     * - `limit`: The maximum number of results to return (e.g. 10, 25, 50, 100).
     * - `offset`: The starting index for results, used for pagination (must be a multiple of `limit` or 0).
     * - `direction`: Sort direction, either `"asc"` or `"desc"`.
     *
     * The tag is only updated if it passes validation via `validateDisplayKey`.
     *
     * Common usage:
     * - Triggered when a user interacts with UI controls like pagination buttons, sort dropdowns, or result limits.
     * - Typically used to reset 'offset' to 0 when a new query parameter is added.
     * - Typically used before or together with `sendQuery` to request updated results.
     *
     * @param {string} key - The display key to update (must be a valid `displayTags` key).
     * @param {string | number} tag - The new value to assign to the display key.
     */
    changeDisplayTag(key, tag) {
      if(validateDisplayKey(key, tag)) {
        this.displayTags[key] = tag
      }
      else {
        console.warn(
          `[ArkivMetaStore] changeDisplayTag: Key "${key}" and Tag ${tag} do not match any known type groups. No action taken.`
        )
      }
    },
    /**
     * ⚠️ **Internal Use Only**
     * 
     * @function buildQueryString
     * @private
     * Constructs a valid ArkivMeta v3 query string from the current `searchTags` and `displayTags` state.
     * 
     * This method prepares and stores the final URL in `this.queryString` for API use. It should **only**
     * be called internally by the `sendQuery` action. Calling it externally may result in inconsistent
     * or invalid store state.
     *
     * Full API documentation: [ArkivMeta Swagger UI](http://load205.kmsext.dk:41502/swagger-ui/index.html)
     *
     * ### Behavior:
     * - **Search Tags (`searchTags`)**:
     *   - `set`: Joined with commas and included only if the Set is non-empty.
     *   - `string`: Included only if non-empty after trimming.
     *   - `number`: Included only if the value is a valid number (not `NaN`).
     *   - All values are URI-encoded before inclusion.
     *   - Unknown or mismatched types are logged and skipped.
     *
     * - **Display Tags (`displayTags`)**:
     *   - All key-value pairs are included as-is, without filtering.
     *
     * ### Query string includes:
     * - The appropriate API endpoint: `kort` or `dokument`, based on the current `isKort` state.
     * - All valid search and display parameters.
     * - An access token from the environment (`VITE_ARKIVMETA_TOKEN`).
     *
     * ### Example Output:
     * ```
     * https://api.dataforsyningen.dk/rest/arkivmeta_test/v3/kort?daekningsomraade=Gr%C3%B8nland&kortgruppe=Flyfotos&limit=50&offset=0&sort=maalestok&direction=desc&token=[valid_token]
     * ```
     */
    async buildQueryString() {
      const searchParams = []
      const displayParams = []

      Object.entries(this.searchTags).forEach(([key, tag]) => {
        switch(getSearchKeyType(key)) {
        case 'set':
          if(tag.size === 0){
            break
          }
          //specific handling for 'kortvaerk' for some reason
          if(key === 'kortvaerk') {
            tag.forEach(tagItem => {
              searchParams.push(`${key}=${encodeURIComponent(tagItem)}`)
            })
          }
          else {
            searchParams.push(`${key}=${encodeURIComponent([...tag].join(','))}`)
          }
          break
        case 'string':
          if(tag.trim() === ''){
            break
          }
          searchParams.push(`${key}=${encodeURIComponent(tag.trim())}`)
          break
        case 'number':
          if(Number.isNaN(tag)){
            break
          }
          searchParams.push(`${key}=${encodeURIComponent(tag)}`)
          break
        default:
          console.warn(
            `[ArkivMetaStore] buildQueryString: Key "${key}" and Tag ${tag} do not match any known type groups. Store state is invalid.`
          )
        } 
      })
      Object.entries(this.displayTags).forEach(([key, tag]) => {
        displayParams.push(`${key}=${tag}`)
      })
      this.queryString = `${import.meta.env.VITE_ARKIVMETA_URL}${this.isKort?'kort':'dokument'}?${searchParams.join('&')}&${displayParams.join('&')}&token=${import.meta.env.VITE_ARKIVMETA_TOKEN}`
      console.log('[ArkivmetaStore] Query: ', this.queryString)
    },
    /**
     * @function buildUrlParams
     * @private
     *
     * Constructs a URL-friendly object representation of the current store state (`searchTags` and `displayTags`),
     * suitable for use with `router.push({ query })` from Vue Router. This ensures that the website's URL remains
     * synchronized with the store's current filter state.
     *
     * This function is called after setting store state from user input before updating the URL. Use outside of this workflow is not recommended
     * as invoking it outside of this context can lead to inconsistencies between the URL and store state.
     * See workflow section of in store.md for more.
     *
     * ### Behavior:
     * - **searchTags**:
     *   - `set`: Converted to a comma-separated string if the Set is non-empty.
     *   - `string`: Included only if non-empty after trimming.
     *   - `number`: Included only if a valid number (not `NaN`).
     *   - Invalid or empty values are skipped.
     *
     * - **displayTags**:
     *   - All key-value pairs are added directly without validation, as types are limited to `string` or `number`.
     *
     * The result is stored in `this.urlParams`, which can be passed directly to the Vue Router's query object.
     *
     * ### Example usage:
     * ```js
     * router.push({ query: arkivmetaStore.getURLParams() })
     * ```
     */
    async buildUrlParams() {
      const tempUrlParams = {}
      Object.entries(this.searchTags).forEach(([key, value]) => {
        switch(getSearchKeyType(key)){
        case 'set':
          if(value.size === 0){
            break
          }
          tempUrlParams[key] = [...value].map(tag => (tag.replace(',', '~'))).join(',')
          break
        case 'string':
          if(value.trim() === ''){
            break
          }
        case 'number':
          if(Number.isNaN(value)){
            break
          }
          tempUrlParams[key] = value
          break
        default:
          break
        
        }
      })
      Object.entries(this.displayTags).forEach(([key, value]) => {
        //no need to distinguish when only types are number and string
        tempUrlParams[key] = encodeURIComponent(value)
      })
      this.urlParams = tempUrlParams
    },
    /**
   * @function sendQuery
   * 
   * ⚠️ **Specific Use Only**
   *
   * This function should only be called from the mounted-hooks in DocumentSearch.vue and MapSearch.vue !
   * 
   * Executes a full ArkivMeta v3 API query based on the current state of the store.
   * 
   * This function is the central mechanism for retrieving search results. It performs the following:
   * 
   * 1. **Prepares Query State**
   *    - Calls `buildQueryString()` to generate a full, valid ArkivMeta v3 URL based on `searchTags` and `displayTags`.
   * 
   * 2. **Executes Network Request**
   *    - Makes a `GET` request to the ArkivMeta endpoint defined in the store using `fetch()`.
   *    - Applies appropriate CORS and JSON headers.
   * 
   * 3. **Handles Response**
   *    - On success: Parses and stores the JSON result in `responseData.value`.
   *    - On failure: Stores the error message in `responseData.error` and logs a warning.
   *    - Updates `responseData.isLoading` to reflect the request status.
   * 
   * ### State Effects:
   * - `this.queryString` is updated with the full URL used.
   * - `this.responseData` reflects the result or error state.
   * 
   * ### Common Usage:
   * This function is typically called after updating `searchTags` and/or `displayTags` via user interaction.
   * the function does not update url params using the function: buildURLParams. It is called from a watcher that listens to url-changes.
   * 
   * Example (setting a new filter and triggering a query):
   * ```js
   * const setChosenKortgruppe = async (chosenKortgruppe) => {
   *   const tag = props.isKort ? 'kortgruppe' : 'dokumentsamling'
   *   arkivMetaStore.addSearchTag(tag, chosenKortgruppe)
   *   arkivMetaStore.changeDisplayTag('sort', chosenKortgruppe === 'Matrikelkort' ? 'maalestok' : 'titel')
   *   arkivMetaStore.changeDisplayTag('offset', 0)
   *   await arkivMetaStore.sendQuery()
   * }
   * ```
   * 
   * @async
   * @returns {Promise<void>}
   */  
    async sendQuery() {
      this.responseData.isLoading = true
      await this.buildQueryString()
      try {
        const response = await fetch(this.queryString, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Origin': window.location.origin
          },
          mode: 'cors',
          credentials: 'omit'
        })
    
        if (!response.ok) {
          console.warn(
            `[ArkivMetaStore] sendQuery: fetch failed for ${this.queryString}. Response was ${response.status} ${response.statusText}`
          )
          this.responseData.error = `HTTP ${response.status}` 
          return
        }
    
        const data = await response.json()
        this.responseData.value = data
      } catch (err) {
        console.error(`[ArkivMetaStore] sendQuery: network error for ${this.queryString}`, err)
        this.responseData.error = err.message 
      }
      finally {
        this.responseData.isLoading = false
      }
    },
    /**
     * @function setStoreStateFromURL
     * 
     * Updates the store’s `searchTags` and `displayTags` state based on a provided query parameter object,
     * typically derived from the current URL (`route.query`).
 
     * This function should only be called **after setting the correct search context**, e.g., via `setDokumentSearch()` or `setKortSearch()`,
     * as it relies on the presence and structure of `searchTags` and `displayTags` to determine which keys are valid.
     * 
     * ### Behavior:
     * - Iterates over each key in `paramObject`:
     *   - If the key exists in `displayTags`, it assigns the value based on the key’s expected type (`string` or `number`).
     *   - If the key exists in `searchTags`, it assigns:
     *     - A new `Set` from comma-separated values (for keys of type `set`),
     *     - A string directly (for `string` keys),
     *     - A number via `Number(tag)` (for `number` keys).
     *   - Invalid or unknown keys are ignored and logged with a warning.
     * 
     * ### Example Usage (in `DocumentSearch.vue`):
     * ```js
     * import { useArkivMetaStore } from '../store/pinia/arkivMetaStore'
     * import { useRoute } from 'vue-router'
     * 
     * const arkivMetaStore = useArkivMetaStore()
     * const route = useRoute()
     * arkivMetaStore.setStoreStateFromURL(route.query)
     * await arkivMetaStore.sendQuery()
     * ```
     * 
     * @param {Object} paramObject - An object representing query parameters from the URL.
     */

    setStoreStateFromURL(paramObject) {
      this.reset()
      const displayKeys = Object.keys(this.displayTags)
      const searchKeys = Object.keys(this.searchTags)
      if(this.searchTags)
        Object.entries(paramObject).forEach(([key, tag]) => {
          if(displayKeys.includes(key)){
            switch(getDisplayKeyType(key)){
            case 'number':
              this.displayTags[key] = decodeURIComponent(Number(tag))
              break
            case 'string':
              this.displayTags[key] = decodeURIComponent(tag)
              break
            default:
              console.warn(
                `[ArkivMetaStore] setStoreStateFromURL: Invalid query param ${key} recieved. Only valid parameters are kept in URL`
              )
              break
            }
          }
          else if(searchKeys.includes(key)){
            switch(getSearchKeyType(key)){
            case 'set':
              this.searchTags[key] = new Set(tag.split(',').map((x) => decodeURIComponent(x.replace(/~/g, ','))))
              break
            case 'string':
              this.searchTags[key] = decodeURIComponent(tag)
              break
            case 'number':
              this.searchTags[key] = Number(decodeURIComponent(tag))
              break
            default:
              console.warn(
                `[ArkivMetaStore] setStoreStateFromURL: Invalid query param ${key} recieved. Only valid parameters are kept in URL`
              )
              break
            }
          }
          else {
            console.warn(
              `[ArkivMetaStore] setStoreStateFromURL: Invalid query param ${key} recieved. Only valid parameters are kept in URL`
            )
          }
        })
    },
  }
})